import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react"
import { supabase } from "@/lib/supabase"
import { useAuth } from "./AuthContext"

export type Theme = "light" | "dark"

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => Promise<void>
  isTransitioning: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const THEME_STORAGE_KEY = "theme"
const DARK_MEDIA_QUERY = "(prefers-color-scheme: dark)"
const THEME_TRANSITION_DURATION = 400

const isTheme = (value: string | null): value is Theme =>
  value === "light" || value === "dark"

const readStoredTheme = (): Theme | null => {
  if (typeof window === "undefined") return null
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    return isTheme(stored) ? stored : null
  } catch {
    return null
  }
}

const getSystemTheme = (): Theme => {
  if (typeof window === "undefined") return "light"
  return window.matchMedia && window.matchMedia(DARK_MEDIA_QUERY).matches
    ? "dark"
    : "light"
}

const applyThemeToDocument = (nextTheme: Theme) => {
  if (typeof document === "undefined") return
  const root = document.documentElement
  root.dataset.theme = nextTheme
  root.classList.toggle("dark", nextTheme === "dark")
  root.style.setProperty("color-scheme", nextTheme)
}

const persistThemeLocally = (theme: Theme) => {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // Ignore storage write failures (e.g., private browsing)
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [theme, setTheme] = useState<Theme>(() => readStoredTheme() ?? getSystemTheme())
  const [hasPersistentPreference, setHasPersistentPreference] = useState<boolean>(
    () => readStoredTheme() !== null,
  )
  const [isTransitioning, setIsTransitioning] = useState(false)
  const transitionTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    applyThemeToDocument(theme)
  }, [theme])

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current && typeof window !== "undefined") {
        window.clearTimeout(transitionTimeoutRef.current)
      }
    }
  }, [])

  const syncAnonymousTheme = useCallback(() => {
    const storedTheme = readStoredTheme()
    if (storedTheme) {
      setTheme(storedTheme)
      setHasPersistentPreference(true)
    } else {
      setHasPersistentPreference(false)
      setTheme(getSystemTheme())
    }
  }, [])

  const toggleTheme = useCallback(async () => {
    const nextTheme: Theme = theme === "light" ? "dark" : "light"
    setIsTransitioning(true)
    setTheme(nextTheme)
    persistThemeLocally(nextTheme)
    setHasPersistentPreference(true)

    if (user) {
      try {
        const { error } = await supabase
          .from("user_profiles")
          .upsert(
            { user_id: user.id, theme_preference: nextTheme },
            { onConflict: "user_id" },
          )

        if (error) {
          throw error
        }
      } catch (err) {
        console.error("Error updating theme:", err)
      }
    }

    if (typeof window !== "undefined") {
      if (transitionTimeoutRef.current) {
        window.clearTimeout(transitionTimeoutRef.current)
      }
      transitionTimeoutRef.current = window.setTimeout(() => {
        setIsTransitioning(false)
        transitionTimeoutRef.current = null
      }, THEME_TRANSITION_DURATION)
    } else {
      setIsTransitioning(false)
    }
  }, [theme, user])

  useEffect(() => {
    if (!user) {
      syncAnonymousTheme()
      return
    }

    let isMounted = true

    const fetchTheme = async () => {
      try {
        const { data, error } = await supabase
          .from("user_profiles")
          .select("theme_preference")
          .eq("user_id", user.id)
          .single()

        if (!isMounted) return

        if (!error && isTheme(data?.theme_preference)) {
          setTheme(data.theme_preference)
          persistThemeLocally(data.theme_preference)
          setHasPersistentPreference(true)
          return
        }

        const storedTheme = readStoredTheme()
        if (storedTheme) {
          setTheme(storedTheme)
          setHasPersistentPreference(true)
          return
        }

        setHasPersistentPreference(false)
        setTheme(getSystemTheme())
      } catch (err) {
        console.error("Error fetching theme:", err)
      }
    }

    fetchTheme()

    return () => {
      isMounted = false
    }
  }, [user, syncAnonymousTheme])

  useEffect(() => {
    if (hasPersistentPreference) return
    if (typeof window === "undefined") return

    const mediaQuery = window.matchMedia(DARK_MEDIA_QUERY)
    const applySystemPreference = () => {
      setTheme(mediaQuery.matches ? "dark" : "light")
    }

    applySystemPreference()

    const handleChange = (event: MediaQueryListEvent) => {
      setTheme(event.matches ? "dark" : "light")
    }

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange)
      return () => mediaQuery.removeEventListener("change", handleChange)
    }

    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [hasPersistentPreference])

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isTransitioning }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider")
  }
  return context
}
